import { defineStoredJson } from '../../utils/storage';
import { reportSyncError, shouldReportSyncError } from './syncProblemReport';

/**
 * What actually went wrong the last time a sync failed — the chip itself can
 * only say "Synchronisierungsfehler".
 *
 * RxDB hands the real reason to `error$` and then throws it away, so it is
 * caught once, at the replication itself, never in a hook — a hook would record
 * the same failure again for every mounted chip. A failing replication retries
 * every five seconds, so identical entries are counted rather than repeated.
 *
 * SHARED BY EVERY WINDOW of this browser, because only one of them replicates
 * (see db/replication): in a tab's own memory the log stays empty wherever the
 * failure did not happen, and the window beside it shows green and knows
 * nothing.
 */

/** One reason a collection could not sync, however often it has recurred. */
export interface SyncErrorEntry {
    /** Stable across counts, so React lists keep their identity. */
    id: number;
    /** RxDB collection name — the technical name, as a report should carry it. */
    collection: string;
    /** 'pull' | 'push' | 'stream' | '' when the error does not say. */
    direction: string;
    /** RxDB's own code (RC_PULL, RC_PUSH, …), empty for a plain Error. */
    code: string;
    /** The one line worth reading. */
    message: string;
    /** Everything else the error carried, one line each. */
    details: string[];
    /** When it first happened, and when it last did. */
    firstAt: number;
    lastAt: number;
    /** How often this exact failure has recurred. */
    count: number;
}

/** More than this and the oldest reason drops off; a retry loop is repetitive. */
const MAX_ENTRIES = 20;

const errorLog = defineStoredJson<SyncErrorEntry[]>({
    key: 'sync_errors',
    fallback: () => [],
    // A diagnostic, never work: a wipe may take it.
    decode: (parsed) =>
        Array.isArray(parsed) ? (parsed as SyncErrorEntry[]) : undefined
});

/**
 * Whether the last thing the sync did was FAIL — the state the chip paints.
 * Shared for the same reason the log is: `error$` and `sent$` only fire in the
 * window that replicates. Written where those events are (db/replication), read
 * by every window's chip.
 */
const failingValue = defineStoredJson<boolean>({
    key: 'sync_failing',
    fallback: () => false,
    decode: (parsed) => (typeof parsed === 'boolean' ? parsed : undefined)
});

/** Fires on a change here and in another window. */
export const subscribeSyncFailing = failingValue.subscribe;

/** Whether the sync is currently in a failed state, whoever hit it. */
export function isSyncFailing(): boolean {
    return failingValue.read();
}

/**
 * Say that the sync just failed — or just succeeded, which is what clears it.
 * Only a real change is written: a retry every five seconds would otherwise
 * wake every window's chip for nothing.
 */
export function markSyncFailing(failing: boolean): void {
    if (failingValue.read() !== failing) {
        failingValue.write(failing);
    }
}

/**
 * The last list handed out, so the same array comes back until something
 * actually changes: `useSyncErrors` feeds it to `useSyncExternalStore`, which
 * compares by identity and would re-render for ever on a merely rebuilt list.
 */
let snapshot: { json: string; entries: SyncErrorEntry[] } | null = null;

/** Fires on a failure recorded HERE and on one recorded in another window. */
export const subscribeSyncErrors = errorLog.subscribe;

/** Newest first. */
export function getSyncErrors(): SyncErrorEntry[] {
    const entries = errorLog.read();
    const json = JSON.stringify(entries);
    if (!snapshot || snapshot.json !== json) {
        snapshot = { json, entries };
    }
    return snapshot.entries;
}

/**
 * A fresh session (or a fresh login) starts with a clean slate. It empties the
 * SHARED log, so a window opened while another shows a failure clears that
 * reason too — one that is still happening writes itself down again within five
 * seconds.
 */
export function clearSyncErrors(): void {
    markSyncFailing(false);
    if (errorLog.read().length === 0) {
        return;
    }
    errorLog.clear();
}

/**
 * Returns whether the failure counted. Offline it does not: every retry fails
 * with "Failed to fetch" by definition, the work waits in the local database,
 * and the chip already says "offline" — logging it would paint the sync red and
 * mail a report about a device doing exactly what offline work means.
 */
export function recordSyncError(collection: string, error: unknown): boolean {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return false;
    }
    const described = describeSyncError(error);
    const now = Date.now();
    const entries = errorLog.read();

    const known = entries.find(
        (entry) =>
            entry.collection === collection &&
            entry.direction === described.direction &&
            entry.message === described.message
    );
    if (known) {
        // Replace rather than mutate: the list is handed to React as a snapshot.
        const repeated = { ...known, lastAt: now, count: known.count + 1 };
        const remaining = entries.filter((entry) => entry !== known);
        errorLog.write([repeated, ...remaining]);
        // A failure that has outlived its retries is reported by the app
        // itself — only from here, never from a first occurrence (see
        // `shouldReportSyncError`).
        if (shouldReportSyncError(repeated, now)) {
            reportSyncError(repeated, [repeated, ...remaining]);
        }
        return true;
    }

    // Numbered from what is stored, not from a counter in this window: two
    // windows writing at once must not hand React two reasons under one key.
    const nextId =
        entries.reduce((highest, entry) => Math.max(highest, entry.id), 0) + 1;
    errorLog.write(
        [
            {
                id: nextId,
                collection,
                ...described,
                firstAt: now,
                lastAt: now,
                count: 1
            },
            ...entries
        ].slice(0, MAX_ENTRIES)
    );
    return true;
}

/** The readable parts of whatever `error$` emitted. */
interface DescribedError {
    direction: string;
    code: string;
    message: string;
    details: string[];
}

const DIRECTION_BY_CODE: Record<string, string> = {
    RC_PULL: 'pull',
    RC_PUSH: 'push',
    RC_STREAM: 'stream',
    RC_UNAUTHORIZED: 'auth'
};

/**
 * Unwrap RxDB's replication error: an `RxError` whose `parameters.errors` holds
 * what the pull or push handler actually threw — the HTTP status, the network
 * failure, the blocked-author message. That inner line is the one worth showing;
 * the wrapper only repeats which side failed.
 *
 * Written against shapes rather than RxDB's types: an error is exactly the value
 * that must never make the code reading it throw.
 */
export function describeSyncError(error: unknown): DescribedError {
    const record =
        error && typeof error === 'object'
            ? (error as Record<string, unknown>)
            : {};
    const code = typeof record.code === 'string' ? record.code : '';
    const parameters =
        record.parameters && typeof record.parameters === 'object'
            ? (record.parameters as Record<string, unknown>)
            : {};
    const direction =
        typeof parameters.direction === 'string'
            ? parameters.direction
            : (DIRECTION_BY_CODE[code] ?? '');

    const inner = Array.isArray(parameters.errors)
        ? parameters.errors.map(errorLine).filter((line) => line !== '')
        : [];
    const outer = errorLine(error);

    // `||`, not `??`: an error that carries no readable text at all yields an
    // empty line here, and a blank entry says even less than the chip did.
    const message = inner[0] || outer || 'Unbekannter Fehler';
    const details = [...inner.slice(1)];
    if (outer !== '' && outer !== message) {
        details.push(outer);
    }
    return { direction, code, message, details };
}

/** One error, as a single line — whatever it turns out to be made of. */
function errorLine(error: unknown): string {
    if (error === null || error === undefined) {
        return '';
    }
    if (typeof error === 'string') {
        return oneLine(error);
    }
    const record = error as Record<string, unknown>;
    const status =
        typeof record.status === 'number' ? ` (${record.status})` : '';
    if (typeof record.message === 'string' && record.message !== '') {
        return oneLine(`${record.message}${status}`);
    }
    // Not an Error at all — a rejected response, a plain object. Show what it
    // holds rather than "[object Object]".
    try {
        return oneLine(JSON.stringify(error));
    } catch {
        return oneLine(String(error));
    }
}

/** RxDB's own messages run over several lines; a log line is one line. */
function oneLine(text: string): string {
    return text.replace(/\s+/g, ' ').trim().slice(0, 300);
}
